
import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, X } from "lucide-react";

interface PageSelectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  totalRecords: number;
  recordsPerPage: number;
  onDownload: (startPage: number, endPage: number) => void;
  reportTitle: string;
}

const PageSelectionDialog = ({ 
  isOpen, 
  onClose, 
  totalRecords, 
  recordsPerPage,
  onDownload,
  reportTitle
}: PageSelectionDialogProps) => {
  const [startPage, setStartPage] = useState(1);
  const [endPage, setEndPage] = useState(1);
  
  const totalPages = Math.ceil(totalRecords / recordsPerPage);

  const handleDownload = () => {
    const validStartPage = Math.max(1, Math.min(startPage, totalPages));
    const validEndPage = Math.max(validStartPage, Math.min(endPage, totalPages));
    
    console.log('Page selection:', { validStartPage, validEndPage, totalPages });
    onDownload(validStartPage, validEndPage);
    onClose();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  console.log('PageSelectionDialog render:', { isOpen, totalRecords, totalPages });

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            Select Pages to Print - {reportTitle}
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-6 w-6 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogTitle>
          <DialogDescription>
            Choose which pages to download ({totalRecords} records, {totalPages} pages)
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="startPage">Start Page</Label>
              <Input
                id="startPage"
                type="number"
                min="1"
                max={totalPages}
                value={startPage}
                onChange={(e) => setStartPage(parseInt(e.target.value) || 1)}
              />
            </div>
            <div>
              <Label htmlFor="endPage">End Page</Label>
              <Input
                id="endPage"
                type="number"
                min="1"
                max={totalPages}
                value={endPage}
                onChange={(e) => setEndPage(parseInt(e.target.value) || 1)}
              />
            </div>
          </div>
          
          <div className="text-sm text-muted-foreground">
            Total Pages Available: {totalPages} | Records per page: {recordsPerPage}
          </div>

          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleDownload} disabled={totalRecords === 0}>
              <Download className="w-4 h-4 mr-2" />
              Continue
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PageSelectionDialog;
